// Glossaire viticole geoVINUM — page publique optimisée SEO/GEO.
//
// Ship:
//   • H1 + résumé LLM-friendly
//   • Filtre par lettre (A-Z) + recherche instantanée + filtre catégorie
//   • Schema.org DefinedTermSet + DefinedTerm JSON-LD (LLM friendly)
//   • Ancres URL uniques par terme pour maillage interne
//   • Related Explorer + CTA Shop
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, BookOpen, ArrowRight, Map as MapIcon, Loader2, AlertCircle } from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import { GLOSSARY, CATEGORIES, GLOSSARY_COUNT } from "@/data/glossaire";
import { useLang } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const CANONICAL_BASE = "https://geovinum.io/glossaire";

// Localized H1 + intro copy per lang. Definition/term content itself
// comes from the /api/glossary endpoint (LLM-translated + cached).
const I18N = {
  fr: {
    h1: "Glossaire du vin",
    summary: (n) => <><strong>{n} termes</strong> définis, couvrant viticulture, vinification, dégustation, terroir, appellations et cépages. Une référence pédagogique conçue pour les élèves WSET Level 1 à 4, les sommeliers, les vignerons et les curieux.</>,
    search: (n) => `Rechercher parmi ${n} termes…`,
    all_cats: "Toutes catégories",
    count: (a, b) => `${a} / ${b} termes`,
    empty: "Aucun terme ne correspond à cette recherche.",
    reset: "Réinitialiser",
    cta_explorer_eyebrow: "Explorer",
    cta_explorer_title: "Parcourez toutes les fiches",
    cta_explorer_desc: "Terroirs, cépages, producteurs — la référence Atlas geoVINUM.",
    cta_shop_eyebrow: "Cartes à offrir",
    cta_shop_title: "Emportez le glossaire au mur",
    cta_shop_desc: "Cartes des vignobles imprimées, éditées par geoVINUM.",
    bc_home: "Accueil",
    bc: "Glossaire du vin",
    loading: "Chargement des traductions…",
    stale_notice: "Certains termes sont affichés en français en attendant la traduction (mise à jour en cours en arrière-plan).",
    meta_desc: (n) => `Glossaire viticole complet : ${n} termes définis, couvrant viticulture, vinification, dégustation, terroir, appellations et cépages. Une référence WSET Level 1 à 4.`,
    title: "Glossaire du vin — geoVINUM",
  },
  en: {
    h1: "Wine glossary",
    summary: (n) => <><strong>{n} defined terms</strong> covering viticulture, winemaking, tasting, terroir, appellations and grape varieties. A pedagogical reference for WSET Level 1 to 4 students, sommeliers, winegrowers and curious minds.</>,
    search: (n) => `Search across ${n} terms…`,
    all_cats: "All categories",
    count: (a, b) => `${a} / ${b} terms`,
    empty: "No term matches this search.",
    reset: "Reset",
    cta_explorer_eyebrow: "Explorer",
    cta_explorer_title: "Browse all wine profiles",
    cta_explorer_desc: "Terroirs, grape varieties, producers — the geoVINUM Atlas reference.",
    cta_shop_eyebrow: "Wine maps",
    cta_shop_title: "Take the glossary to the wall",
    cta_shop_desc: "Printed wine-region maps, edited by geoVINUM.",
    bc_home: "Home",
    bc: "Wine glossary",
    loading: "Loading translations…",
    stale_notice: "Some terms remain in French while translation is running in the background.",
    meta_desc: (n) => `Complete wine glossary: ${n} defined terms covering viticulture, winemaking, tasting, terroir, appellations and grape varieties. A WSET Level 1 to 4 reference.`,
    title: "Wine Glossary — geoVINUM",
  },
  es: {
    h1: "Glosario del vino",
    summary: (n) => <><strong>{n} términos</strong> definidos que cubren viticultura, vinificación, cata, terroir, denominaciones y variedades. Una referencia pedagógica para alumnos WSET nivel 1 a 4, sumilleres, viticultores y curiosos.</>,
    search: (n) => `Buscar entre ${n} términos…`,
    all_cats: "Todas las categorías",
    count: (a, b) => `${a} / ${b} términos`,
    empty: "Ningún término coincide con esta búsqueda.",
    reset: "Reiniciar",
    cta_explorer_eyebrow: "Explorer",
    cta_explorer_title: "Explora todas las fichas",
    cta_explorer_desc: "Terroirs, variedades, productores — la referencia Atlas geoVINUM.",
    cta_shop_eyebrow: "Mapas para regalar",
    cta_shop_title: "Cuelga el glosario en la pared",
    cta_shop_desc: "Mapas de viñedos impresos, editados por geoVINUM.",
    bc_home: "Inicio",
    bc: "Glosario del vino",
    loading: "Cargando traducciones…",
    stale_notice: "Algunos términos permanecen en francés mientras la traducción se ejecuta en segundo plano.",
    meta_desc: (n) => `Glosario del vino completo: ${n} términos definidos que cubren viticultura, vinificación, cata, terroir, denominaciones y variedades. Referencia WSET nivel 1 a 4.`,
    title: "Glosario del vino — geoVINUM",
  },
  pt: {
    h1: "Glossário do vinho",
    summary: (n) => <><strong>{n} termos</strong> definidos que abrangem viticultura, vinificação, prova, terroir, denominações e castas. Uma referência pedagógica para alunos WSET Nível 1 a 4, sommeliers, vinicultores e curiosos.</>,
    search: (n) => `Pesquisar entre ${n} termos…`,
    all_cats: "Todas as categorias",
    count: (a, b) => `${a} / ${b} termos`,
    empty: "Nenhum termo corresponde a esta pesquisa.",
    reset: "Redefinir",
    cta_explorer_eyebrow: "Explorer",
    cta_explorer_title: "Percorra todas as fichas",
    cta_explorer_desc: "Terroirs, castas, produtores — a referência Atlas geoVINUM.",
    cta_shop_eyebrow: "Mapas para oferecer",
    cta_shop_title: "Leve o glossário para a parede",
    cta_shop_desc: "Mapas de vinhedos impressos, editados por geoVINUM.",
    bc_home: "Início",
    bc: "Glossário do vinho",
    loading: "A carregar traduções…",
    stale_notice: "Alguns termos permanecem em francês enquanto a tradução decorre em segundo plano.",
    meta_desc: (n) => `Glossário do vinho completo: ${n} termos definidos que abrangem viticultura, vinificação, prova, terroir, denominações e castas. Referência WSET Nível 1 a 4.`,
    title: "Glossário do vinho — geoVINUM",
  },
};

const GlossairePage = () => {
  const [q, setQ] = useState("");
  const [letter, setLetter] = useState("");
  const [category, setCategory] = useState("");
  const lang = useLang() || "fr";
  const tr = I18N[lang] || I18N.fr;

  // Localized category labels — the source `CATEGORIES` array is FR
  // only; we overlay translations here so the badges + filter dropdown
  // follow the site language.
  const CAT_I18N = {
    fr: {},
    en: { viti: "Viticulture", vini: "Winemaking", degustation: "Tasting", terroir: "Terroir & soils", appellation: "Appellations & law", business: "Business & market", defaut: "Faults & diseases", cepage: "Grape varieties", service: "Service & pairing" },
    es: { viti: "Viticultura", vini: "Vinificación", degustation: "Cata", terroir: "Terroir y suelos", appellation: "Denominaciones y derecho", business: "Negocio y mercado", defaut: "Defectos y enfermedades", cepage: "Variedades", service: "Servicio y maridaje" },
    pt: { viti: "Viticultura", vini: "Vinificação", degustation: "Prova", terroir: "Terroir e solos", appellation: "Denominações e direito", business: "Negócio e mercado", defaut: "Defeitos e doenças", cepage: "Castas", service: "Serviço e harmonização" },
  };
  const catLabel = (key, fallback) => (CAT_I18N[lang]?.[key]) || fallback;

  // Localized glossary — FR uses the local bundle instantly (no roundtrip).
  // Other langs fetch /api/glossary?lang=xx which is Mongo-cached +
  // LLM-translated on first miss. FR fallback for stale entries.
  const [remoteTerms, setRemoteTerms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasStale, setHasStale] = useState(false);
  const CANONICAL = `${CANONICAL_BASE}${lang !== "fr" ? `?lang=${lang}` : ""}`;

  useEffect(() => {
    if (lang === "fr") {
      setRemoteTerms(null);
      setHasStale(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { ok, data } = await apiFetch(`/api/glossary?lang=${lang}`);
      if (cancelled) return;
      if (ok && data?.terms) {
        setRemoteTerms(data.terms);
        setHasStale(data.terms.some((t) => t.stale));
      } else {
        setRemoteTerms(null);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [lang]);

  // Merge remote translations into the local structure (keeping slug +
  // category + wset + letter from the FR source). This preserves the
  // alphabet ordering + category badges without another data trip.
  const effectiveGlossary = useMemo(() => {
    if (lang === "fr" || !remoteTerms) return GLOSSARY;
    const bySlug = new Map(remoteTerms.map((t) => [t.slug, t]));
    return GLOSSARY.map((t) => {
      const r = bySlug.get(t.slug);
      if (!r) return t;
      return {
        ...t,
        term: r.term || t.term,
        definition: r.definition || t.definition,
        stale: !!r.stale,
        // Re-derive letter from the translated term so the alphabet
        // index groups by the shown initial (not the FR one).
        letter: (r.term || t.term).normalize("NFD").replace(/[\u0300-\u036f]/g, "").charAt(0).toUpperCase(),
      };
    }).sort((a, b) => (a.term || "").localeCompare(b.term || "", lang, { sensitivity: "base" }));
  }, [remoteTerms, lang]);

  useEffect(() => {
    document.title = tr.title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = tr.meta_desc(GLOSSARY_COUNT);
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.rel = "canonical"; document.head.appendChild(c); }
    c.href = CANONICAL;

    // hreflang alternates — one <link> per supported locale + one
    // x-default. Overrides the global tags in `public/index.html`
    // (which point to `/`) so Google understands that the 4 URL
    // variants of `/glossaire` are localized alternates of the same
    // page. Tagged with `data-glossary-hreflang` so we can clean them
    // up on unmount / lang change without touching global tags.
    const marker = "data-glossary-hreflang";
    document.head
      .querySelectorAll(`link[${marker}]`)
      .forEach((el) => el.remove());
    const langs = [
      ["fr", `${CANONICAL_BASE}`],
      ["en", `${CANONICAL_BASE}?lang=en`],
      ["es", `${CANONICAL_BASE}?lang=es`],
      ["pt", `${CANONICAL_BASE}?lang=pt`],
      ["x-default", `${CANONICAL_BASE}`],
    ];
    for (const [code, href] of langs) {
      const l = document.createElement("link");
      l.rel = "alternate";
      l.hreflang = code;
      l.href = href;
      l.setAttribute(marker, "1");
      document.head.appendChild(l);
    }

    // Handle direct URL anchor (e.g. /glossaire#kimmeridgien).
    const hash = window.location.hash?.replace("#", "");
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
    // Cleanup on unmount so we don't pollute other pages' <head>.
    return () => {
      document.head
        .querySelectorAll(`link[${marker}]`)
        .forEach((el) => el.remove());
    };
  }, [tr, CANONICAL]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return effectiveGlossary.filter((t) => {
      if (letter && t.letter !== letter) return false;
      if (category && t.category !== category) return false;
      if (nq) {
        const hay = `${t.term} ${t.definition}`.toLowerCase();
        if (!hay.includes(nq)) return false;
      }
      return true;
    });
  }, [q, letter, category, effectiveGlossary]);

  const availableLetters = useMemo(() => {
    return new Set(effectiveGlossary.map((t) => t.letter));
  }, [effectiveGlossary]);

  const byLetter = useMemo(() => {
    const map = new Map();
    filtered.forEach((t) => {
      if (!map.has(t.letter)) map.set(t.letter, []);
      map.get(t.letter).push(t);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, lang));
  }, [filtered, lang]);

  // Schema.org DefinedTermSet + DefinedTerm — the LLM-friendly gold
  // standard for glossaries. Each term is emitted as a DefinedTerm with
  // its own URL fragment, definition, and inLanguage tag.
  const inLang = { fr: "fr-FR", en: "en-US", es: "es-ES", pt: "pt-PT" }[lang];
  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: tr.title,
    description: tr.meta_desc(GLOSSARY_COUNT),
    url: CANONICAL,
    inLanguage: inLang,
    hasDefinedTerm: effectiveGlossary.map((t) => ({
      "@type": "DefinedTerm",
      name: t.term,
      description: t.definition,
      inDefinedTermSet: CANONICAL,
      url: `${CANONICAL_BASE}#${t.slug}`,
      termCode: t.slug,
      inLanguage: inLang,
    })),
  }), [tr, effectiveGlossary, CANONICAL, inLang]);

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: tr.bc_home, item: "https://geovinum.io/" },
      { "@type": "ListItem", position: 2, name: tr.bc, item: CANONICAL },
    ],
  };

  return (
    <Layout testId="glossaire-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

      {/* Hero */}
      <header className="bg-[color:var(--gv-mist)]/40 border-b border-[color:var(--gv-line)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
          <Breadcrumb items={[{ label: tr.bc_home, to: "/" }, { label: tr.bc }]} />
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--gv-ink)] mt-4 mb-4" data-testid="glossaire-h1">
            {tr.h1}
          </h1>
          <p className="text-lg text-[color:var(--gv-ink)]/80 max-w-3xl leading-relaxed" data-testid="glossaire-summary">
            {tr.summary(GLOSSARY_COUNT)}
          </p>
          {loading && (
            <div className="mt-4 inline-flex items-center gap-2 text-[13px] text-[color:var(--gv-ink)]/60" data-testid="glossaire-loading">
              <Loader2 size={12} className="animate-spin" /> {tr.loading}
            </div>
          )}
          {hasStale && !loading && (
            <div className="mt-4 inline-flex items-start gap-2 text-[12.5px] text-[color:var(--gv-ink)]/70 border border-amber-300/60 bg-amber-50/50 rounded-[5px] px-3 py-2 max-w-2xl" data-testid="glossaire-stale">
              <AlertCircle size={13} className="text-amber-500 shrink-0 mt-0.5" /> {tr.stale_notice}
            </div>
          )}
        </div>
      </header>

      {/* Filters */}
      <div className="sticky top-[72px] z-20 bg-white border-b border-[color:var(--gv-line)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tr.search(GLOSSARY_COUNT)}
              className="w-full pl-9 pr-3 py-2 border border-[color:var(--gv-line)] rounded-[5px] text-[13.5px]"
              data-testid="glossaire-search"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="border border-[color:var(--gv-line)] rounded-[5px] px-2 py-2 text-[13px] bg-white"
            data-testid="glossaire-category"
          >
            <option value="">{tr.all_cats}</option>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{catLabel(c.key, c.label)}</option>)}
          </select>
          <div className="flex flex-wrap gap-0.5" data-testid="glossaire-alphabet">
            {ALPHABET.map((L) => {
              const has = availableLetters.has(L);
              return (
                <button
                  key={L}
                  disabled={!has}
                  onClick={() => setLetter(letter === L ? "" : L)}
                  className={`w-7 h-7 rounded text-[11.5px] font-medium transition-colors ${
                    letter === L
                      ? "bg-[color:var(--gv-pink)] text-white"
                      : has
                      ? "text-[color:var(--gv-ink)]/70 hover:bg-[color:var(--gv-mist)]"
                      : "text-[color:var(--gv-ink)]/25 cursor-not-allowed"
                  }`}
                  data-testid={`glossaire-letter-${L}`}
                >
                  {L}
                </button>
              );
            })}
          </div>
          <span className="text-[11.5px] text-[color:var(--gv-ink)]/55 ml-auto" data-testid="glossaire-count">
            {tr.count(filtered.length, GLOSSARY_COUNT)}
          </span>
        </div>
      </div>

      {/* Term list */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        {byLetter.length === 0 ? (
          <div className="text-center py-16 text-[color:var(--gv-ink)]/50" data-testid="glossaire-empty">
            {tr.empty} <button onClick={() => { setQ(""); setLetter(""); setCategory(""); }} className="text-[color:var(--gv-pink)] underline">{tr.reset}</button>
          </div>
        ) : (
          byLetter.map(([L, terms]) => (
            <section key={L} id={`letter-${L}`} className="mb-10">
              <h2 className="font-display text-3xl text-[color:var(--gv-pink)] border-b-2 border-[color:var(--gv-pink)] pb-1 mb-5 w-10" data-testid={`glossaire-section-${L}`}>{L}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                {terms.map((t) => {
                  const cat = CATEGORIES.find((c) => c.key === t.category);
                  return (
                    <article key={t.slug} id={t.slug} className="scroll-mt-32" data-testid={`glossaire-term-${t.slug}`}>
                      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                        <h3 className="font-display text-[17px] text-[color:var(--gv-ink)]">
                          <a href={`#${t.slug}`} className="hover:text-[color:var(--gv-pink)]">{t.term}</a>
                        </h3>
                        {cat && (
                          <span
                            className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: `${cat.color}15`, color: cat.color }}
                          >
                            {catLabel(cat.key, cat.label)}
                          </span>
                        )}
                        <span className="text-[10px] text-[color:var(--gv-ink)]/40">WSET {t.wset}</span>
                        {t.stale && <span className="text-[9.5px] uppercase tracking-widest text-amber-600 border border-amber-300 rounded px-1">FR</span>}
                      </div>
                      <p className="text-[14px] text-[color:var(--gv-ink)]/80 leading-relaxed">{t.definition}</p>
                    </article>
                  );
                })}
              </div>
            </section>
          ))
        )}

        {/* Related links */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 pt-8 border-t border-[color:var(--gv-line)]">
          <Link to="/blog" className="border border-[color:var(--gv-line)] rounded-[6px] p-5 hover:border-[color:var(--gv-pink)] transition-colors" data-testid="glossaire-cta-explorer">
            <div className="flex items-center gap-2 mb-1 text-[color:var(--gv-pink)]">
              <MapIcon size={14} /> <span className="text-[11px] uppercase tracking-widest">{tr.cta_explorer_eyebrow}</span>
            </div>
            <div className="font-display text-lg text-[color:var(--gv-ink)]">{tr.cta_explorer_title}</div>
            <div className="text-[13px] text-[color:var(--gv-ink)]/60 mt-1">{tr.cta_explorer_desc} <ArrowRight size={11} className="inline ml-1" /></div>
          </Link>
          <Link to="/shop" className="border border-[color:var(--gv-line)] rounded-[6px] p-5 hover:border-[color:var(--gv-pink)] transition-colors" data-testid="glossaire-cta-shop">
            <div className="flex items-center gap-2 mb-1 text-[color:var(--gv-pink)]">
              <BookOpen size={14} /> <span className="text-[11px] uppercase tracking-widest">{tr.cta_shop_eyebrow}</span>
            </div>
            <div className="font-display text-lg text-[color:var(--gv-ink)]">{tr.cta_shop_title}</div>
            <div className="text-[13px] text-[color:var(--gv-ink)]/60 mt-1">{tr.cta_shop_desc} <ArrowRight size={11} className="inline ml-1" /></div>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default GlossairePage;
